import { ArrowRight } from "lucide-react";
import { useState } from "react";

import {
	type SingleStageRuntimeKind,
	SingleStageRuntimePanel,
	type SingleStageRuntimeTargetStage,
} from "@/components/shared/single-stage-runtime-panel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { intentLabel, playbookLabel, sampleLabel } from "@/lib/product-labels";

type SingleStageTestPanelProps = {
	testId: string;
	kind: SingleStageRuntimeKind;
	title: string;
	description: string;
	targetStage: SingleStageRuntimeTargetStage;
	stageLabel: string;
	sampleId: string;
	sampleMessage: string;
	expectedIntent: string;
	expectedPlaybook: string;
	assetName: string;
	boundary: string;
	datasetHref: string;
	samples?: SingleStageTestSample[];
};

export type SingleStageTestSample = {
	sampleId: string;
	sampleMessage: string;
	expectedIntent: string;
	expectedPlaybook: string;
	scenario: string;
	verificationPoint: string;
	assetName: string;
	boundary: string;
};

export function SingleStageTestPanel({
	testId,
	kind,
	title,
	description,
	targetStage,
	stageLabel,
	sampleId,
	sampleMessage,
	expectedIntent,
	expectedPlaybook,
	assetName,
	boundary,
	datasetHref,
	samples,
}: SingleStageTestPanelProps) {
	const fallbackSample: SingleStageTestSample = {
		sampleId,
		sampleMessage,
		expectedIntent,
		expectedPlaybook,
		scenario: stageLabel,
		verificationPoint: boundary,
		assetName,
		boundary,
	};
	const allSamples = samples && samples.length > 0 ? samples : [fallbackSample];
	const [selectedSampleId, setSelectedSampleId] = useState(allSamples[0]?.sampleId ?? sampleId);
	const selectedSample =
		allSamples.find((item) => item.sampleId === selectedSampleId) ?? fallbackSample;

	return (
		<section className="space-y-4" data-testid={testId}>
			<Card className="border-brand-warning/30 bg-brand-warning/10 shadow-none">
				<CardHeader>
					<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
						<div className="min-w-0 space-y-2">
							<CardTitle className="text-base text-brand-warning">{title}</CardTitle>
							<CardDescription className="max-w-3xl leading-6 text-brand-warning">
								{description}
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					{allSamples.length > 1 ? (
						<div
							className="rounded-md border border-brand-warning/30 bg-white/80 p-3"
							data-testid={`single-stage-sample-selector-${kind}`}
						>
							<div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
								<div>
									<div className="text-sm font-semibold text-brand-warning">测试样本集</div>
									<p className="mt-1 text-xs leading-5 text-brand-warning">
										先选择要验证的业务子场景，再运行本页能力。局部通过不等于端到端通过。
									</p>
								</div>
								<div className="text-xs font-medium text-brand-warning">
									当前：{sampleLabel(selectedSample.sampleId)}
								</div>
							</div>
							<div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
								{allSamples.map((item) => {
									const active = item.sampleId === selectedSample.sampleId;
									return (
										<button
											key={item.sampleId}
											type="button"
											data-testid={`single-stage-sample-option-${item.sampleId}`}
											onClick={() => setSelectedSampleId(item.sampleId)}
											className={`rounded-md border p-3 text-left text-sm transition-colors ${
												active
													? "border-brand-warning bg-brand-warning/10 text-brand-warning"
													: "border-brand-warning/30 bg-white text-brand-warning hover:bg-brand-warning/10"
											}`}
										>
											<div className="font-semibold">{sampleLabel(item.sampleId)}</div>
											<div className="mt-1 text-xs leading-5">{item.scenario}</div>
										</button>
									);
								})}
							</div>
						</div>
					) : null}
					<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
						<Fact label="测试环节" value={stageLabel} detail="只看本环节是否达到预期。" />
						<Fact
							label="当前样本"
							value={sampleLabel(selectedSample.sampleId)}
							detail={selectedSample.sampleMessage}
						/>
						<Fact
							label="预期结果"
							value={intentLabel(selectedSample.expectedIntent)}
							detail={`方案：${playbookLabel(selectedSample.expectedPlaybook)}`}
						/>
						<Fact
							label="责任资产"
							value={selectedSample.assetName}
							detail={selectedSample.boundary}
						/>
					</div>
					{allSamples.length > 1 ? (
						<div
							className="overflow-hidden rounded-md border border-brand-warning/30 bg-white/80"
							data-testid={`single-stage-scenario-matrix-${kind}`}
						>
							<div className="border-b border-brand-warning/30 px-3 py-2 text-sm font-semibold text-brand-warning">
								子场景覆盖
							</div>
							<div className="divide-y divide-brand-warning/30">
								{allSamples.map((item) => (
									<div
										key={item.sampleId}
										className="grid gap-2 px-3 py-2 text-xs leading-5 text-brand-warning md:grid-cols-[1.1fr_1fr_1fr]"
									>
										<div>
											<div className="font-semibold text-brand-warning">{item.scenario}</div>
											<div>{sampleLabel(item.sampleId)}</div>
										</div>
										<div>
											<div>{intentLabel(item.expectedIntent)}</div>
											<div>{playbookLabel(item.expectedPlaybook)}</div>
										</div>
										<div>{item.verificationPoint}</div>
									</div>
								))}
							</div>
						</div>
					) : null}
					<div className="rounded-md border border-brand-warning/30 bg-white/80 p-3 text-sm leading-6 text-brand-warning">
						本页直接承接当前能力的单环节验证；通过后再进入评测运营承接候选。
						端到端回复效果仍回到链路测试台和评测运行确认。
					</div>
					<div className="flex flex-wrap gap-2">
						<PanelLink href={datasetHref}>查看样本合同</PanelLink>
						<PanelLink href="/pipeline-testbench?mock=1">跑端到端链路</PanelLink>
					</div>
				</CardContent>
			</Card>
			<SingleStageRuntimePanel
				kind={kind}
				targetStage={targetStage}
				sampleId={selectedSample.sampleId}
				sampleMessage={selectedSample.sampleMessage}
				expectedIntent={selectedSample.expectedIntent}
				expectedPlaybook={selectedSample.expectedPlaybook}
				assetName={selectedSample.assetName}
				boundary={selectedSample.boundary}
			/>
		</section>
	);
}

function Fact({ label, value, detail }: { label: string; value: string; detail: string }) {
	return (
		<div className="rounded-md border border-brand-warning/30 bg-white/80 p-3">
			<div className="text-xs font-medium text-brand-warning">{label}</div>
			<div className="mt-2 break-words text-sm font-semibold leading-5 text-brand-warning">
				{value}
			</div>
			<p className="mt-2 text-xs leading-5 text-brand-warning">{detail}</p>
		</div>
	);
}

function PanelLink({ href, children }: { href: string; children: string }) {
	return (
		<a
			href={href}
			className="inline-flex items-center rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-subtle"
		>
			{children}
			<ArrowRight className="ml-2 h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
		</a>
	);
}
